import { UserState, userStore } from '@store/user';
import { useLocalStorageStore } from '@utilities/hooks/use-local-storage-store';
import createStore from '@utilities/store';

import {
    areaNewsletterApiCalls,
    AreaProjectNewsletterProps,
    ConstructionNumberNewsletterProps,
    ProjectPhaseNewsletterProps,
    PropertyTypeNewsletterProps,
} from './services/api-calls';
import {
    SubscriptionStoreAddress,
    SubscriptionStoreState,
    SubscriptionStoreUser,
} from './store.types';

const initialState: SubscriptionStoreState = {
    areaProject: false,
    projectPhases: [],
    propertyTypes: [],
    isUpdatedFromUserStore: false,
};

const UPDATE = 'update';
const UPDATE_BATCH = 'updateBatch';
const ON_LOGIN = 'onLogin';
const ON_LOGOUT = 'onLogout';

const actions = {
    init: (state: SubscriptionStoreState, payload: SubscriptionStoreState) => {
        state.user = payload.user;
        state.address = payload.address;
        state.areaProject = payload.areaProject;
        state.projectPhases = payload.projectPhases;
        state.propertyTypes = payload.propertyTypes;
        state.isUpdatedFromUserStore = payload.isUpdatedFromUserStore;

        return state;
    },
    [UPDATE_BATCH]: (
        state: SubscriptionStoreState,
        {
            user,
            address,
            projectPhases,
            propertyTypes,
            areaProject,
        }: Partial<SubscriptionStoreState>,
    ) => {
        if (areaProject) state.areaProject = true;

        if (projectPhases?.length) {
            state.projectPhases = [...state.projectPhases, ...projectPhases];
        }

        if (propertyTypes?.length) {
            state.propertyTypes = [...state.propertyTypes, ...propertyTypes];
        }

        if (user) {
            state.user = {
                ...state.user,
                ...user,
            };
        }

        if (address) {
            state.address = {
                ...state.address,
                ...address,
            };
        }

        return state;
    },
    [UPDATE]: (
        state: SubscriptionStoreState,
        {
            areaInfo,
            userInfo,
            address,
        }: AreaProjectNewsletterProps &
            ProjectPhaseNewsletterProps &
            PropertyTypeNewsletterProps &
            ConstructionNumberNewsletterProps,
    ) => {
        if (areaInfo) {
            const { areaProjectId, projectPhaseId, propertyTypeId } = areaInfo;

            /*
                If one of the following id's is included, the user will be subscribed to one of those newsletters
            */
            if (areaProjectId) state.areaProject = true;
            if (propertyTypeId) {
                state.propertyTypes = [...state.propertyTypes, propertyTypeId];
            } else if (projectPhaseId) {
                state.projectPhases = [...state.projectPhases, projectPhaseId];
            }
        }

        if (userInfo) {
            state.user = {
                ...state.user,
                ...userInfo,
            };
        }

        if (address) {
            state.address = {
                ...state.address,
                ...address,
            };
        }

        return state;
    },
    [ON_LOGIN]: (state: SubscriptionStoreState, userState: UserState) => {
        if (userState.info) {
            const {
                email,
                initials,
                firstName,
                lastName,
                gender,
                subscribedToAreaNewsletter,
                zipcode,
                street,
                houseNumber,
                houseNumberSuffix,
                city,
                phoneNumber,
                country,
            } = userState.info;

            state.address = {
                city,
                zipcode,
                street,
                houseNumber,
                houseNumberAddition: houseNumberSuffix,
                phoneNumber,
                country,
            };

            state.user = {
                email,
                initials,
                firstName,
                lastName,
                gender,
            };

            state.areaProject = !!subscribedToAreaNewsletter;
            state.projectPhases = userState.projects?.map(({ Id }) => Id) ?? [];
            state.propertyTypes = userState.propertyTypes?.map(({ Id }) => Id) ?? [];
            state.isUpdatedFromUserStore = true;
        }

        return state;
    },
    [ON_LOGOUT]: (state: SubscriptionStoreState) => {
        state.projectPhases = [];
        state.propertyTypes = [];
        state.areaProject = false;
        state.address = undefined;
        state.user = undefined;
        state.isUpdatedFromUserStore = false;

        return state;
    },
};

export const subscriptionStore = useLocalStorageStore({
    key: 'subscription',
    store: createStore<SubscriptionStoreState>({
        initialState,
        actions,
    }),
    expireInDays: 30,
});

/*
    When logging in, fill the store with the available data
    When logging out, empty the store
*/
userStore.subscribe(
    (userState) => {
        if (userState.loggedIn) {
            // Only update it from the user store once
            // on a new page view, new user api call will be done
            // which is not up to date yet with the latest newsletter subscription
            if (!getSubscriptionState().isUpdatedFromUserStore)
                subscriptionStore.dispatch(ON_LOGIN, userState);
        } else {
            subscriptionStore.dispatch(ON_LOGOUT);
        }
    },
    ['loggedIn'],
);

export const subscribeToAreaProject = async (props: AreaProjectNewsletterProps) => {
    try {
        await areaNewsletterApiCalls.areaProject(props);
        subscriptionStore.dispatch(UPDATE, props);
    } catch {}
};

export const subscribeToProjectPhase = async (props: ProjectPhaseNewsletterProps) => {
    try {
        await areaNewsletterApiCalls.projectPhase(props);
        subscriptionStore.dispatch(UPDATE, props);
    } catch {}
};

export const subscribeToAreaProjectAndProjectPhases = async (
    projectPhasesProps: ProjectPhaseNewsletterProps[],
    areaProjectProps?: AreaProjectNewsletterProps,
) => {
    const stagedUpdate: Partial<SubscriptionStoreState> = {};
    try {
        if (areaProjectProps) {
            await areaNewsletterApiCalls.areaProject(areaProjectProps);
            stagedUpdate.areaProject = true;
            stagedUpdate.user = {
                ...areaProjectProps.userInfo,
            };
        }

        stagedUpdate.address = projectPhasesProps[0].address;
        stagedUpdate.projectPhases = [];

        for (const props of projectPhasesProps) {
            await areaNewsletterApiCalls.projectPhase(props);
            stagedUpdate.projectPhases.push(props.areaInfo.projectPhaseId);
        }

        subscriptionStore.dispatch(UPDATE_BATCH, stagedUpdate);
    } catch {}
};

export const subscribeToPropertyType = async (props: PropertyTypeNewsletterProps) => {
    try {
        await areaNewsletterApiCalls.propertyType(props);
        subscriptionStore.dispatch(UPDATE, props);
    } catch {}
};

export const subscribeToPropertyTypes = async (
    propertyTypesProps: PropertyTypeNewsletterProps[],
) => {
    const stagedUpdate: Partial<SubscriptionStoreState> = {};
    try {
        stagedUpdate.propertyTypes = [];

        for (const props of propertyTypesProps) {
            await areaNewsletterApiCalls.propertyType(props);
            stagedUpdate.propertyTypes.push(props.areaInfo.propertyTypeId);
        }

        subscriptionStore.dispatch(UPDATE_BATCH, stagedUpdate);
    } catch {}
};

export const subscribeToConstructionNumber = async (props: ConstructionNumberNewsletterProps) => {
    try {
        await areaNewsletterApiCalls.constructionNumber(props);
        subscriptionStore.dispatch(UPDATE, props);
    } catch {}
};

export const isSubscribedToAreaProject = () => subscriptionStore.getState().areaProject;

export const isSubscribedToPropertyType = (propertyTypeId: string) =>
    subscriptionStore.getState().propertyTypes.includes(propertyTypeId);

export const isSubscribedToProjectPhase = (projectPhaseId: string) =>
    subscriptionStore.getState().projectPhases.includes(projectPhaseId);

export const getSubscriptionUser = () => subscriptionStore.getState().user;

export const getSubscriptionAddress = () => subscriptionStore.getState().address;

export const getSubscriptionState = () => subscriptionStore.getState();

export const isStoreUserDataComplete = (user?: SubscriptionStoreUser) => {
    if (!user) return false;
    const { email, firstName, lastName, gender } = user;
    return !!(email && firstName && lastName && gender);
};

export const isStoreAddressDataComplete = (address?: SubscriptionStoreAddress) => {
    if (!address) return false;
    const { zipcode, phoneNumber, street, houseNumber, city } = address;
    return !!(zipcode && street && houseNumber && city && phoneNumber);
};

export const isSubscriptionFormRequired = (): { area: boolean; project: boolean } => {
    const userInfo = getSubscriptionUser();
    const addressInfo = getSubscriptionAddress();

    const hasUserInfo = isStoreUserDataComplete(userInfo);
    const hasAddressInfo = isStoreAddressDataComplete(addressInfo);

    return { area: !hasUserInfo, project: !(hasUserInfo && hasAddressInfo) };
};
